'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import NavMarker from 'react-rhumb/NavMarker';
import PropTypes from 'prop-types';
import unescapedText from 'I18n/utils/unescapedText';
import { parse as parseParams } from 'hub-http/helpers/params';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Loadable from 'UIComponents/decorators/Loadable';
import Review from 'ecosystem-marketplace-core/records/Review';
import { getDisplayedReviewerName } from 'ecosystem-marketplace-core/utils/i18n';
import { submitReview } from '../../actions/ReviewsActions';
import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import * as ReviewTypes from 'marketplace-ui-reviews/constants/ReviewType';
import { getUserEmail, getUserFirstName, getUserLastName } from '../../selectors/auth';
import ThemeReviewability from '../../records/ThemeReviewability';
const AsyncReviewWizard = Loadable({
  loader: () => import(
  /* webpackChunkName: 'review-wizard' */
  'ecosystem-marketplace-core/components/reviews/wizard/theme/ThemeReviewWizard')
});

const ReviewWizard = ({
  name,
  offeringId,
  offeringType,
  onClose,
  onSuccess,
  onTrackInteraction,
  providerName,
  reviewability,
  slug,
  supportLink,
  thumbnailUrl,
  versionNumber,
  reviewType
}) => {
  const dispatch = useDispatch();
  useEffect(() => {
    if (!reviewability.isReviewable()) {
      dispatch(postNotification({
        id: 'ASSET_UNREVIEWABLE_NOTIFICATION',
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "marketplace-assets-ui.themes.details.reviews.unreviewable.titleText"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `marketplace-assets-ui.themes.details.reviews.unreviewable.${reviewability.getUnreviewableReason()}`,
          options: {
            offeringType: unescapedText(`marketplace-assets-ui.details.offeringType.${offeringType}_lowercase`)
          }
        })
      }));
    }
  }, [offeringType, reviewability, dispatch]);
  const userEmail = useSelector(getUserEmail);
  const firstName = useSelector(getUserFirstName);
  const lastName = useSelector(getUserLastName);
  const displayName = getDisplayedReviewerName({
    firstName,
    lastName
  });
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const overallRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const defaultReview = useMemo(() => {
    const {
      portalEmployeeSize: employeeSize,
      portalIndustry: industry
    } = reviewability;
    const hasDefaultCompanyInfo = employeeSize || industry;
    const storedReview = Review.from(JSON.parse(sessionStorage.getItem(`${slug}_review`)));
    let displayedReview = storedReview;

    if (hasDefaultCompanyInfo) {
      displayedReview = displayedReview.setEmployees(employeeSize).setIndustry(industry);
    }

    if (overallRating) {
      displayedReview = displayedReview.setOverallRating(overallRating);
    }

    return displayedReview;
  }, [overallRating, reviewability, slug]);
  const handleSubmit = useCallback(review => {
    return dispatch(submitReview(review.set('createdAt', new Date().valueOf()).set('reviewerDisplayName', displayName).set('offeringId', offeringId).set('reviewType', reviewType))).then(() => {
      onSuccess(review);
    });
  }, [dispatch, displayName, offeringId, reviewType, onSuccess]);
  const updateSessionStorage = useCallback(updatedReview => {
    sessionStorage.setItem(`${slug}_review`, JSON.stringify(updatedReview));
  }, [slug]);
  const onWizardLoad = useCallback(() => {
    onTrackInteraction('open review wizard', {
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onStepChange = useCallback((prevStep, nextStep) => {
    onTrackInteraction('change review wizard step', {
      value: nextStep,
      prevStep,
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onReject = useCallback(() => {
    onTrackInteraction('dismiss review wizard', {
      reviewSource
    });
    onClose();
  }, [onTrackInteraction, onClose, reviewSource]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: reviewability.isReviewable() && /*#__PURE__*/_jsx(AsyncReviewWizard, {
      reviewableName: name,
      reviewableIconUrl: thumbnailUrl,
      reviewableProvider: providerName,
      failed: false,
      defaultReview: defaultReview,
      displayName: displayName,
      loading: false,
      onReject: onReject,
      onChange: updateSessionStorage,
      onConfirm: handleSubmit,
      reviewType: reviewType,
      onStepChange: onStepChange,
      onWizardLoad: onWizardLoad,
      supportLink: supportLink,
      userEmail: userEmail,
      versionNumber: versionNumber
    })
  });
};

ReviewWizard.propTypes = {
  name: PropTypes.string.isRequired,
  offeringId: PropTypes.number.isRequired,
  offeringType: PropTypes.oneOf(['MODULE', 'THEME']).isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onTrackInteraction: PropTypes.func.isRequired,
  providerName: PropTypes.string.isRequired,
  reviewability: PropTypes.instanceOf(ThemeReviewability).isRequired,
  reviewType: PropTypes.oneOf(Object.values(ReviewTypes)).isRequired,
  slug: PropTypes.string.isRequired,
  supportLink: PropTypes.string,
  thumbnailUrl: PropTypes.string.isRequired,
  versionNumber: PropTypes.number.isRequired
};
export default ReviewWizard;